<script setup>
  const { searchInputOnPage } = useSearchOverlay();
  const input = ref();

  onMounted(() => {
    searchInputOnPage.value = input.value;
  });

  onBeforeUnmount(() => {
    searchInputOnPage.value = null;
  });

  // --

  const api = useApi();
  const route = useRoute();
  const router = useRouter();

  // --

  const items = ref({});
  const paginateResults = ref({});
  const totalItems = ref(0);
  const itemsPerPage = 5;

  const newQuery = ref(route.query.q ? route.query.q : '');
  const currentQuery = ref(route.query.q ? route.query.q : '');
  const currentPage = ref(route.query.page ? parseInt(route.query.page) : 1);

  // search

  const search = async () => {
    items.value = await api.statamicSearch({ query: newQuery.value });

    if (currentQuery.value !== newQuery.value) {
      currentPage.value = 1;
    }

    currentQuery.value = newQuery.value;
    totalItems.value = items.value.meta.total;

    await paginate(currentPage.value);
  };

  // pagination

  const paginate = async (current) => {
    paginateResults.value = items.value.data.slice((current - 1) * itemsPerPage, current * itemsPerPage);
    router.replace({ query: { q: currentQuery.value, page: current } });
  };

  provide('totalItems', totalItems);
  provide('currentPage', currentPage);
  provide('paginate', paginate);

  provide('searchQuery', newQuery);

  // init

  onBeforeMount(async () => {
    await search(currentQuery.value);
  });
</script>

<!----------------------------------------------------------------------------->

<template>
  <Section autowrap wide class="search">
    <BigTitle h1>
      <slot />
    </BigTitle>
    <Foreground>
      <form @submit.prevent="search">
        <SearchInput ref="input" :model="newQuery" @closeClick="newQuery = ''" />
        <div class="result-count">
          <b>{{ totalItems }}</b> {{ totalItems === 1 ? 'Result' : 'Results'
          }}<span v-if="newQuery !== ''">
            for <b>“{{ currentQuery }}”</b></span
          >
        </div>
      </form>
    </Foreground>
  </Section>
  <Section autowrap wide class="results">
    <ul>
      <ShowAnimation v-for="item in paginateResults" :key="item.id">
        <li>
          <NuxtLink :to="item.url">
            <label>{{ item.type }}</label>
            <h3>{{ item.title }}</h3>
          </NuxtLink>
        </li>
      </ShowAnimation>
    </ul>

    <Pagination
      v-if="totalItems > itemsPerPage"
      :url="`/search?q=${currentQuery}&page=[page]`"
      :totalItems="totalItems"
      :itemsPerPage="itemsPerPage"
    />
  </Section>
</template>

<!----------------------------------------------------------------------------->

<style lang="scss" scoped>
  .search {
    @include top-padding;
    padding-bottom: 0;

    margin-bottom: -px(70);

    @include break-small {
      margin-bottom: 0;
    }

    .foreground {
      margin-top: px(90);

      @include break-small {
        margin-top: px(4);
      }
    }

    form {
      background-color: $black-5;
      padding: px(32);

      margin-left: -px(32);
      margin-right: -px(32);

      border-radius: px(2);

      @include break-medium {
        margin-left: -px(16);
        margin-right: -px(16);
      }

      @include break-small {
        padding: px(16);
        border-radius: 0;
      }

      @include break-phone {
        margin-left: -px(10);
        margin-right: -px(10);
      }
    }

    .result-count {
      position: absolute;
      right: 0;

      color: $black;
      font-size: px(14);
      margin-top: px(52);

      @include break-small {
        position: relative;
        color: $white;

        font-size: px(12);

        margin-top: px(24);
        margin-bottom: px(8);
      }
    }
  }

  .results {
    background-color: $white;
    color: $black;

    padding-top: px(128);
    margin-bottom: -$content-padding-bottom;

    @include break-small {
      padding-top: px(12);
    }

    ul {
      li {
        width: 100%;

        // &:last-child {
        //   a {
        //     border-bottom: 0;
        //   }
        // }

        a {
          display: block;
          border-bottom: px(1) solid $grey-4;

          padding: px(38) 0 px(30);

          @include break-large {
            padding: px(34) 0 px(28);
          }

          @include break-medium {
            padding: px(28) 0 px(26);
          }

          @include break-small {
            padding: px(16) 0 px(12);
          }

          label {
            display: inline-flex;

            @include industry-demi(px(10));
            white-space: nowrap;

            border: px(1) solid $grey-1;

            padding: px(4) px(8) px(2);
            margin-bottom: px(28);

            @include break-large {
              margin-bottom: px(24);
            }

            @include break-large {
              margin-bottom: px(20);
            }

            @include break-small {
              margin-bottom: px(16);
            }
          }

          h3 {
            @include title-l;

            @include break-large {
              font-size: 7vw;
            }

            @include break-medium {
              font-size: 6vw;
            }

            @include break-small {
              font-size: px(48);
            }

            @include break-phone {
              font-size: px(40);
            }
          }
        }
      }
    }
  }
</style>
